/**
 * Quando o servidor diz não — e quando não conseguimos falar com ele.
 *
 * **A `mensagem` é a tela.** Ela vem do banco com o nome da pergunta já
 * interpolado, e é por isso que não há aqui nenhum mapa de `motivo` para texto
 * nosso. Um rótulo local diria "Este formulário está encerrado" e o convidado
 * perderia o que a frase do banco tem de acionável. Pior: `valor_invalido`
 * nasceu no banco depois desta tela, e um mapa local o mostraria vazio ou
 * genérico.
 *
 * ── A exceção, e por que ela é uma correção e não uma brecha ────────────────
 *
 * A DATA da janela é a única coisa que a tela acrescenta, e é porque o banco não
 * pode escrevê-la certo. `to_char()` formata no fuso do SERVIDOR — UTC, no
 * Supabase — e o convidado no Brasil lia três horas a mais e achava que ainda
 * dava tempo. A pior forma de errar: ele não vê erro nenhum, só perde o prazo.
 *
 * Quem sabe o fuso de quem está lendo é este navegador. Então o banco manda o
 * INSTANTE (`quando`) e de que beira da janela ele é (`lado`), e a frase da data
 * nasce aqui, com `dataHora()`. A `mensagem` continua completa e verdadeira sem
 * ela — o que mantém a regra do §7.3 de pé: nada aqui REESCREVE o que o banco
 * disse; a data é uma segunda frase, ao lado.
 *
 * O que a tela PODE decidir é a moldura: se há um botão, e qual. Isso é
 * aparência, não conteúdo, e por isso é a única coisa que olha o `motivo`.
 */

import { dataHora } from '../../util/formato';
import type { MotivoConhecido, RecusaDoConvite } from '../../tipos';

/**
 * Os motivos em que reabrir o link resolve — e só eles ganham botão.
 *
 * É uma LISTA e não um mapa indexado por `motivo`: a união é aberta de
 * propósito, e `RECOMECAR.some(...)` compara sem exigir que todo motivo futuro
 * tenha uma entrada aqui. Motivo desconhecido cai fora da lista e a tela fica
 * sem botão, que é o comportamento certo para quem não sabe o que aconteceu.
 *
 * `sessao_invalida` está aqui porque a própria mensagem do banco manda fazer
 * isto: "Abra o link do convite de novo." O botão obedece à frase em vez de
 * substituí-la.
 */
const RECOMECAR: readonly MotivoConhecido[] = ['sessao_invalida'];

export type PropsDaRecusa = {
  readonly recusa: RecusaDoConvite;
  readonly aoRecomecar: () => void;
};

/**
 * A frase da data, no fuso de quem lê — ou nada.
 *
 * Só escreve quando o banco mandou os DOIS campos e `lado` é um dos dois valores
 * conhecidos. `lado` novo, ou `quando` sozinho, não vira frase inventada: a
 * união do que o banco manda é aberta de propósito, e o silêncio é a degradação
 * segura. A `mensagem` já disse o essencial.
 */
function frasesDaJanela(recusa: RecusaDoConvite): string | null {
  if (!recusa.quando) return null;
  const quando = dataHora(recusa.quando);
  // `dataHora` devolve o travessão quando não consegue ler o instante, e um
  // "Abrem em —" é pior do que não dizer nada.
  if (quando === '—') return null;
  if (recusa.lado === 'abertura') return `Abrem em ${quando}.`;
  if (recusa.lado === 'encerramento') return `Encerraram em ${quando}.`;
  return null;
}

export function Recusa({ recusa, aoRecomecar }: PropsDaRecusa) {
  const podeRecomecar = RECOMECAR.some((motivo) => motivo === recusa.motivo);
  const janela = frasesDaJanela(recusa);

  return (
    <section className="recusa" role="status">
      <p className="recusa__mensagem">{recusa.mensagem}</p>
      {janela === null ? null : <p className="recusa__quando">{janela}</p>}
      {podeRecomecar ? (
        <div className="acoes">
          <button className="botao botao--primario" type="button" onClick={aoRecomecar}>
            Abrir o convite de novo
          </button>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Falha de rede na abertura. Diferente da recusa em duas coisas que importam:
 * não é decisão de ninguém, e tentar de novo pode funcionar — então há botão, e
 * o texto é nosso, porque o servidor não chegou a dizer nada.
 */
export function Falha({
  mensagem,
  aoTentarDeNovo,
}: {
  readonly mensagem: string;
  readonly aoTentarDeNovo: () => void;
}) {
  return (
    <section className="recusa" role="alert">
      <p className="recusa__rotulo">Sem conexão</p>
      <p className="recusa__mensagem">{mensagem}</p>
      <div className="acoes">
        <button className="botao botao--primario" type="button" onClick={aoTentarDeNovo}>
          Tentar de novo
        </button>
      </div>
    </section>
  );
}
